<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  reloadKey: {
    type: Number,
    default: 0,
  },
  conversationUuid: {
    type: String,
    default: '',
  },
  src: {
    type: String,
    default: '',
  },
  method: {
    type: String,
    default: 'GET',
  },
  account: {
    type: String,
    default: '',
  },
  contact: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['toggle']);

const { currentAccount } = useAccount();

const iframeName = 'contact-embed-frame';
const formRef = ref(null);

const normalizeUrl = url => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('//')) return `https:${url}`;

  return `https://${url}`;
};

const actionUrl = computed(() => normalizeUrl(props.src));

const normalizedMethod = computed(() =>
  (props.method || 'GET').toString().trim().toUpperCase()
);
const isPost = computed(() => normalizedMethod.value === 'POST');
const cmsProjectId = computed(() => currentAccount.value?.site_id || '');
const hasConversationUuid = computed(() => !!props.conversationUuid);
const canLoad = computed(
  () =>
    props.isOpen &&
    props.src &&
    cmsProjectId.value &&
    (props.account || props.contact) &&
    props.conversationUuid
);
const isConfigured = computed(() => !!props.src);
const hasAccountIdentifier = computed(() => !!props.account);
const hasContactIdentifier = computed(() => !!props.contact);
const hasProjectId = computed(() => !!cmsProjectId.value);

const actionUrlWithParams = computed(() => {
  if (!props.src) return '';
  if (!hasProjectId.value) return '';
  if (!hasAccountIdentifier.value && !hasContactIdentifier.value) return '';
  if (!hasConversationUuid.value) return '';

  const url = new URL(actionUrl.value);
  url.searchParams.set('cms_project_id', cmsProjectId.value);
  if (hasAccountIdentifier.value)
    url.searchParams.set('account', props.account);
  if (!hasAccountIdentifier.value && hasContactIdentifier.value)
    url.searchParams.set('contact', props.contact);
  url.searchParams.set('conversation_uuid', props.conversationUuid);
  return url.toString();
});

const iframeSrc = computed(() => {
  if (!actionUrlWithParams.value) return '';
  if (isPost.value) return '';

  return actionUrlWithParams.value;
});

const submitForm = async () => {
  if (!canLoad.value) return;
  if (!isPost.value) return;

  await nextTick();
  formRef.value?.submit?.();
};

watch(
  () => [
    props.isOpen,
    props.src,
    props.account,
    props.contact,
    props.conversationUuid,
    cmsProjectId.value,
  ],
  () => {
    submitForm();
  }
);
</script>

<template>
  <div>
    <button
      type="button"
      class="flex items-center justify-between w-full rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak m-0 cursor-pointer py-2 px-4"
      @click.stop="emit('toggle')"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span class="i-lucide-panel-right text-n-slate-11 text-base" />
        <h5 class="text-n-slate-12 text-sm mb-0 py-0 pr-2 pl-0 truncate">
          {{ $t('CONVERSATION.SIDEBAR.EMBED.TITLE') }}
        </h5>
      </div>
      <div class="flex justify-end w-3 text-n-blue-text">
        <fluent-icon v-if="isOpen" size="24" icon="subtract" type="solid" />
        <fluent-icon v-else size="24" icon="add" type="solid" />
      </div>
    </button>

    <div
      v-if="isOpen"
      class="bg-n-background outline outline-1 outline-n-weak -mt-[-1px] border-t-0 rounded-br-lg rounded-bl-lg p-2"
    >
      <p v-if="!isConfigured" class="text-sm text-n-slate-11 mb-0 px-2 py-1">
        {{ $t('CONVERSATION.SIDEBAR.EMBED.NOT_CONFIGURED') }}
      </p>
      <p
        v-else-if="!hasProjectId"
        class="text-sm text-n-slate-11 mb-0 px-2 py-1"
      >
        {{ $t('CONVERSATION.SIDEBAR.ORDER_PANEL.NO_PROJECT') }}
      </p>
      <p
        v-else-if="!hasAccountIdentifier && !hasContactIdentifier"
        class="text-sm text-n-slate-11 mb-0 px-2 py-1"
      >
        {{ $t('CONVERSATION.SIDEBAR.EMBED.NO_ACCOUNT_INFO') }}
      </p>
      <p
        v-else-if="!hasConversationUuid"
        class="text-sm text-n-slate-11 mb-0 px-2 py-1"
      >
        {{ $t('CONVERSATION.SIDEBAR.EMBED.NO_CONVERSATION') }}
      </p>
      <p
        v-else-if="normalizedMethod !== 'GET' && normalizedMethod !== 'POST'"
        class="text-sm text-n-slate-11 mb-0 px-2 py-1"
      >
        {{ $t('CONVERSATION.SIDEBAR.EMBED.POST_NOT_SUPPORTED') }}
      </p>
      <div
        v-else
        class="w-full h-[560px] max-h-[65vh] rounded-md overflow-hidden"
      >
        <form
          ref="formRef"
          :action="actionUrlWithParams"
          method="POST"
          :target="iframeName"
          class="hidden"
        />
        <iframe
          :key="reloadKey"
          :name="iframeName"
          class="w-full h-full bg-n-background"
          :src="iframeSrc"
          :title="$t('CONVERSATION.SIDEBAR.EMBED.TITLE')"
          allow="clipboard-write"
        />
      </div>
    </div>
  </div>
</template>
